import { MatrixEvent, RelationType } from 'matrix-js-sdk';
import { ChatMessage } from './chat-message';

/**
 * Chat-Raum mit Array aus ChatMessages.
 * Begründung: Keine AFO (Bestandteil der Redux-Architektur).
 */
export class ChatRoom {
  private constructor(private readonly _messages: ChatMessage[]) {}

  get messages(): ChatMessage[] {
    return this._messages;
  }

  static new(): ChatRoom {
    return new ChatRoom([]);
  }

  /**
   * Holt nur Nachrichten vom Typ m.room.message, d. h. filtert Infomeldungen aus.
   */
  getRoomMessages(): MatrixEvent[] {
    // Redactete Nachrichten ausblenden (konsistent mit der UI, die isRedacted() auswertet).
    // Wichtig für editierte Nachrichten (TIME-4164): Wird das Original redacted, ist zwar
    // first.isRedacted() true, curr (das Edit-Event) trägt seinen Text aber u.U. noch –
    // ohne diesen Filter lieferte getMessages weiterhin den (Edit-)Text aus.
    return this._messages
      .filter(message => !message.isRedacted())
      .map(message => message.curr)
      .filter(event => event.getType() === 'm.room.message');
  }

  /****************************************************************************
   * Reducers
   ****************************************************************************/

  /**
   * Füge neue ChatMessage hinzu.
   */
  reduceByNewMessageEvent(event: MatrixEvent): ChatRoom {
    const eventId = event.getId();
    if (!eventId) {
      console.warn('reduceByNewMessageEvent: eventId fehlt', event);
      return this;
    }

    return this.reduceByMessageEvent(event, eventId);
  }

  /**
   * Erzwingt State-Update für ein in-place entschlüsseltes Event.
   * Das Event-Objekt ist dasselbe (gleiche Referenz), wurde aber vom SDK mutiert.
   * Eine neue ChatRoom-Instanz erzwingt, dass distinctUntilChanged() den Change erkennt.
   */
  reduceByDecryptedEvent(event: MatrixEvent): ChatRoom {
    const eventId = event.getId();
    if (!eventId) {
      return this;
    }
    const messageIndex = this._messages.findIndex(m => m.originalId === eventId);
    if (messageIndex === -1) {
      return this;
    }
    const newMessages = [...this._messages];
    newMessages[messageIndex] = this._messages[messageIndex].cloneForDecryption();
    return new ChatRoom(newMessages);
  }

  /**
   * Erweitere bestehende ChatMessage um neues Event.
   */
  reduceByEditMessageEvent(event: MatrixEvent): ChatRoom {
    const origEventId = event.getContent()['m.relates_to']?.event_id;
    if (!origEventId) {
      console.warn('reduceByEditMessageEvent: origEventId fehlt', event);
      return this;
    }

    return this.reduceByMessageEvent(event, origEventId);
  }

  private reduceByMessageEvent(event: MatrixEvent, origEventId: string): ChatRoom {
    // Suche nach Event. Setzt echte Event-ID voraus. Events mit temporärer ID werden immer als ganz neu angenommen.
    // Unter der Haube ersetzt Matrix etwas später die temporäre ID durch eine echte ID.
    const existingMessage = this.findMessageByOriginalEventId(origEventId, this._messages);

    let newMessages: ChatMessage[];
    if (existingMessage) {
      // Entweder ist das MessageEvent doppelt oder es wurde bereits ein EditEvent eingespeist.
      const newMessage = existingMessage.reduceByMessageEvent(event);

      if (newMessage === existingMessage) {
        // Entweder redacted oder Event bereits eingespeist
        return new ChatRoom([...this._messages]);
      }

      // Ersetze existingMessage durch neue ChatMessage mit erweiterter Event-Liste.
      newMessages = this._messages.map(m => (m === existingMessage ? newMessage : m));
    } else {
      // Wenn keine ChatMessage zu origEventId gefunden wird, erweitere um neue ChatMessage.
      newMessages = [...this._messages, ChatMessage.new(event)];
    }

    // Sortiere nach Timestamps.
    newMessages.sort((a, b) => a.first.getTs() - b.first.getTs());

    return new ChatRoom(newMessages);
  }

  /**
   * Füge Reaction zu Reactions hinzu.
   */
  reduceByReactionEvent(event: MatrixEvent): ChatRoom {
    const rel = event.getRelation();
    // 'm.annotation'
    if (rel?.rel_type !== RelationType.Annotation) {
      console.warn('reduceByReactionEvent: Annotation erwartet.', event);
      return this;
    }

    const origEventId = rel.event_id;
    if (!origEventId) {
      console.warn('reduceByReactionEvent: origEventId fehlt.', event);
      return this;
    }

    const origMessage = this.findMessageByOriginalEventId(origEventId, this._messages);
    if (!origMessage) {
      console.warn('reduceByReactionEvent: Message zu origEventId nicht gefunden.', event);
      return this;
    }
    const newOrigMessage = origMessage.reduceByReactionEvent(event);
    if (newOrigMessage === origMessage) {
      return this;
    }

    const newMessages = this._messages.map(m => (m === origMessage ? newOrigMessage : m));
    return new ChatRoom(newMessages);
  }

  /**
   * Wenn sich die Redaction auf eine Reaction bezieht:
   * Entferne referenzierte Reaction aus Reactions.
   * Wenn sich die Redaction auf eine Message bezieht: Markiere ChatMessage
   * als gelöscht und notiere Reason aus Redaction an ChatMessage.
   */
  reduceByRedactionEvent(event: MatrixEvent): ChatRoom {
    const redactedId = event.event?.redacts;
    if (!redactedId) {
      console.warn('reduceByRedactionEvent: redactedId fehlt.', event);
      return this;
    }

    // redactedId in ChatMessages?
    // Dann markiere ChatMessage als gelöscht mit Reason aus Event.
    let origMessage = this.findMessageByOriginalEventId(redactedId, this._messages);
    if (origMessage) {
      const newOrigMessage = origMessage.reduceByRedactMessageEvent(event);
      if (newOrigMessage === origMessage) {
        return this;
      } else {
        const newMessages = this._messages.map(m => (m === origMessage ? newOrigMessage : m));
        return new ChatRoom(newMessages);
      }
    }

    // Reaction mit redactedId in einer der ChatMessages? Dann entferne Reaction.
    // Suche ChatMessage mit passender Reaction.
    origMessage = this.findMessageWithReaction(redactedId, this._messages);
    if (origMessage) {
      const newOrigMessage = origMessage.reduceByRedactReactionEvent(event);
      if (newOrigMessage === origMessage) {
        return this;
      } else {
        const newMessages = this._messages.map(m => (m === origMessage ? newOrigMessage : m));
        return new ChatRoom(newMessages);
      }
    }

    // Beim Einspeisen der Events aus Timeline (zur Initialisierung):
    // Normaler Zustand bei Redaction auf Reaction, die in der Vergangenheit
    // redacted worden ist. Eine redacted Reaction wird gar nicht erst
    // in die Liste der Reactions zu einer Message aufgenommen.
    // Die spätere Redaction findet daher auch nicht die zugehörige Reaction.

    return this;
  }

  /**
   * Suche ChatMessage mit Original-Nachricht mit event_id = origEventId.
   */
  private findMessageByOriginalEventId(origEventId: string, messages: ChatMessage[]): ChatMessage | undefined {
    return messages.find(m => m.originalId === origEventId);
  }

  private findMessageWithReaction(reactionId: string, messages: ChatMessage[]): ChatMessage | undefined {
    return messages.find(m => m.hasReaction(reactionId));
  }
}
